// MultiSettingsPanel.js
// -----------------------------------------------------------------------------
// The per-session settings form. Used in two places:
//
//   1. MultiCaseBuilder — inline, while the block is being put together.
//   2. MultiBreakScreen — inside a modal, between cases.
//
// It's a controlled component: it never stores settings itself, it just renders
// the object it's given and calls onChange with the next one. Persistence is the
// caller's business (the builder holds them until create; the break screen saves
// them straight to the session).
//
// Props:
//   settings   object                                    (required)
//   onChange   (nextSettings) => void                    (required)
//   disabled   bool — lock the form while saving         (optional)
// -----------------------------------------------------------------------------

import React from "react";

// Mirrors the Custom Time Limit dropdown in NewCaseList's sidebar so the two
// screens offer the same choices. "custom" swaps in a free-text minutes box.
const TIME_LIMIT_OPTIONS = [
  { value: "none", label: "Default for each case (10 or 20 min)" },
  { value: "8", label: "8 minutes" },
  { value: "9", label: "9 minutes" },
  { value: "10", label: "10 minutes" },
  { value: "15", label: "15 minutes" },
  { value: "18", label: "18 minutes" },
  { value: "19", label: "19 minutes" },
  { value: "20", label: "20 minutes" },
  { value: "25", label: "25 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "40", label: "40 minutes" },
  { value: "pct_10", label: "+10% of default" },
  { value: "pct_15", label: "+15% of default" },
  { value: "pct_20", label: "+20% of default" },
  { value: "pct_25", label: "+25% of default" },
  { value: "pct_33", label: "+33% of default" },
  { value: "pct_50", label: "+50% of default" },
];

const NETWORK_LAG_OPTIONS = [
  { value: "none", label: "None" },
  { value: "1", label: "1 second" },
  { value: "2", label: "2 seconds" },
  { value: "3", label: "3 seconds" },
  { value: "4", label: "4 seconds" },
  { value: "5", label: "5 seconds" },
  { value: "6", label: "6 seconds" },
  { value: "random", label: "Random" },
];

const BREAK_MINUTE_OPTIONS = [1, 2, 5, 10, 15];

function isPresetTimeLimit(value) {
  return TIME_LIMIT_OPTIONS.some(function (option) {
    return option.value === value;
  });
}

class MultiSettingsPanel extends React.Component {
  constructor(props) {
    super(props);

    // A time limit that isn't one of the presets came from the "Custom..."
    // box, so reopen the box with that value already in it.
    const custom = !isPresetTimeLimit(props.settings.CustomTimeLimit);
    this.state = {
      showCustomTime: custom,
      customTimeValue: custom ? props.settings.CustomTimeLimit : "",
    };
  }

  patch = (changes) => {
    this.props.onChange(Object.assign({}, this.props.settings, changes));
  };

  handleTimeLimitChange = (e) => {
    const value = e.target.value;
    if (value === "custom") {
      // Hold the previous limit until a number is typed — an empty custom box
      // shouldn't wipe the setting.
      this.setState({ showCustomTime: true, customTimeValue: "" });
      return;
    }
    this.setState({ showCustomTime: false, customTimeValue: "" });
    this.patch({ CustomTimeLimit: value });
  };

  handleCustomTimeChange = (e) => {
    const raw = e.target.value;
    this.setState({ customTimeValue: raw });
    if (raw === "") return;
    const minutes = Math.max(1, parseFloat(raw));
    if (isNaN(minutes)) return;
    this.patch({ CustomTimeLimit: String(minutes) });
  };

  render() {
    const settings = this.props.settings;
    const disabled = !!this.props.disabled;
    const { showCustomTime, customTimeValue } = this.state;

    return (
      <div className="mccs-settings">
        <div className="mccs-field">
          <label className="mccs-check">
            <input
              type="checkbox"
              checked={settings.EnforceCaseTimeLimit}
              disabled={disabled}
              onChange={(e) => {
                this.patch({ EnforceCaseTimeLimit: e.target.checked });
              }}
            />
            Enforce the time limit on each case
          </label>
          <p className="mccs-hint">
            {settings.EnforceCaseTimeLimit
              ? "When the clock runs out, the case ends and is graded automatically."
              : "Cases run untimed. You end each one yourself."}
          </p>
        </div>

        {settings.EnforceCaseTimeLimit && (
          <div className="mccs-field">
            <label className="mccs-label" htmlFor="mccs-time-limit">
              Time limit
            </label>
            <select
              id="mccs-time-limit"
              className="mccs-select"
              disabled={disabled}
              value={showCustomTime ? "custom" : settings.CustomTimeLimit}
              onChange={this.handleTimeLimitChange}
            >
              {TIME_LIMIT_OPTIONS.map(function (option) {
                return (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                );
              })}
              <option value="custom">Custom...</option>
            </select>
            {showCustomTime && (
              <input
                type="number"
                className="mccs-input"
                min="1"
                max="120"
                placeholder="Minutes per case"
                aria-label="Custom minutes per case"
                disabled={disabled}
                value={customTimeValue}
                onChange={this.handleCustomTimeChange}
              />
            )}
          </div>
        )}

        <div className="mccs-field">
          <label className="mccs-check">
            <input
              type="checkbox"
              checked={settings.EnforceBreak}
              disabled={disabled}
              onChange={(e) => {
                this.patch({ EnforceBreak: e.target.checked });
              }}
            />
            Time the break between cases
          </label>
          <p className="mccs-hint">
            {settings.EnforceBreak
              ? "The next case starts on its own when the break runs out."
              : "Breaks are untimed. You start the next case when you're ready."}
          </p>
        </div>

        {settings.EnforceBreak && (
          <div className="mccs-field">
            <label className="mccs-label" htmlFor="mccs-break-minutes">
              Break length
            </label>
            <select
              id="mccs-break-minutes"
              className="mccs-select"
              disabled={disabled}
              value={String(settings.BreakMinutes)}
              onChange={(e) => {
                this.patch({ BreakMinutes: parseInt(e.target.value, 10) });
              }}
            >
              {BREAK_MINUTE_OPTIONS.map(function (minutes) {
                return (
                  <option key={minutes} value={String(minutes)}>
                    {minutes} {minutes === 1 ? "minute" : "minutes"}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        <div className="mccs-field">
          <span className="mccs-label">On the break screen</span>
          <label className="mccs-check">
            <input
              type="checkbox"
              checked={settings.HideCaseTitle}
              disabled={disabled}
              onChange={(e) => {
                this.patch({ HideCaseTitle: e.target.checked });
              }}
            />
            Hide case titles
          </label>
          <label className="mccs-check">
            <input
              type="checkbox"
              checked={settings.HideCaseCategory}
              disabled={disabled}
              onChange={(e) => {
                this.patch({ HideCaseCategory: e.target.checked });
              }}
            />
            Hide case categories
          </label>
          <p className="mccs-hint">
            Hiding both keeps you from guessing at a case before you start it.
          </p>
        </div>

        <div className="mccs-field">
          <label className="mccs-label" htmlFor="mccs-network-lag">
            Simulate network lag
          </label>
          <select
            id="mccs-network-lag"
            className="mccs-select"
            disabled={disabled}
            value={settings.NetworkLag}
            onChange={(e) => {
              this.patch({ NetworkLag: e.target.value });
            }}
          >
            {NETWORK_LAG_OPTIONS.map(function (option) {
              return (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              );
            })}
          </select>
        </div>
      </div>
    );
  }
}

export default MultiSettingsPanel;